import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '@/editor/math/Line2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getPointFromRatio } from '@/editor/math/shape/other';
import { globalStores } from '@/editor/stores';
import { EAbsorbPointType, EAbsorbSource, ISublineInfo } from '@/editor/types/absorb';
import { IShapeSeg2d } from '@/editor/types/shape';

export function getExtremePointNormalLine(wallSeg: IShapeSeg2d) {
  if (wallSeg instanceof ArcSeg2d) {
    const startNormalDirection = Point2dUtils.getUnitDirection(wallSeg.start, wallSeg.center);
    const endNormalDirection = Point2dUtils.getUnitDirection(wallSeg.end, wallSeg.center);
    const startNormalLine = new Line2d({
      parametric: { point: wallSeg.start, direction: startNormalDirection }
    });
    const endNormalLine = new Line2d({
      parametric: { point: wallSeg.end, direction: endNormalDirection }
    });
    return [startNormalLine, endNormalLine];
  } else {
    const wallDirection = Point2dUtils.getDirection(wallSeg.start, wallSeg.end);
    const normalDirection = Point2dUtils.rotateDirection(wallDirection, Math.PI / 2);
    const startNormalLine = new Line2d({
      parametric: { point: wallSeg.start, direction: normalDirection }
    });
    const endNormalLine = new Line2d({
      parametric: { point: wallSeg.end, direction: normalDirection }
    });
    return [startNormalLine, endNormalLine];
  }
}

export function getChordEndpointNormalLines(start: IPoint2d, end: IPoint2d): [Line2d, Line2d] | null {
  if (Point2dUtils.equals(start, end)) return null;

  const chordDirection = Point2dUtils.getUnitDirection(start, end);
  const normalDirection = Point2dUtils.rotateDirection(chordDirection, Math.PI / 2);
  return [
    new Line2d({
      parametric: { point: start, direction: normalDirection }
    }),
    new Line2d({
      parametric: { point: end, direction: normalDirection }
    })
  ];
}

export function getOrthogonalPoint(referencePoint: IPoint2d, rawPoint: IPoint2d) {
  if (Point2dUtils.equals(referencePoint, rawPoint)) return rawPoint;

  const horizontalLine = new Line2d({ parametric: { point: referencePoint, direction: { x: 1, y: 0 } } });
  const verticalLine = new Line2d({ parametric: { point: referencePoint, direction: { x: 0, y: 1 } } });
  const horizontalLineFootPoint = horizontalLine.pointProjectOnLine(rawPoint);
  const verticalLineFootPoint = verticalLine.pointProjectOnLine(rawPoint);
  const toHorizontalLineDistance = Point2dUtils.distanceTo(horizontalLineFootPoint, rawPoint);
  const toVerticalLineDistance = Point2dUtils.distanceTo(verticalLineFootPoint, rawPoint);

  if (toHorizontalLineDistance < toVerticalLineDistance) {
    return horizontalLineFootPoint;
  } else {
    return verticalLineFootPoint;
  }
}

export function getOffsetLineSegByPoint(start: IPoint2d, end: IPoint2d, rawPoint: IPoint2d) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lenSquared = dx * dx + dy * dy;
  const wx = rawPoint.x - start.x;
  const wy = rawPoint.y - start.y;
  const t = (wx * dx + wy * dy) / lenSquared;

  // 计算投影点 foot（即 rowPoint 到原线段所在直线的垂足）
  const foot = {
    x: start.x + t * dx,
    y: start.y + t * dy
  };

  // 平移向量 = rowPoint - foot（沿法向方向）
  const shift = {
    x: rawPoint.x - foot.x,
    y: rawPoint.y - foot.y
  };
  // 计算新的墙体线段
  const shadowWallPosition = {
    start: { x: start.x + shift.x, y: start.y + shift.y },
    end: { x: end.x + shift.x, y: end.y + shift.y }
  };

  return shadowWallPosition;
}

// 获取切线段垂足点
export function getTangentFootPoint(wallSeg: IShapeSeg2d, point: IPoint2d, ratio: number): IPoint2d {
  if (ratio === 0) return wallSeg.start;
  if (ratio === 1) return wallSeg.end;

  if (wallSeg instanceof ArcSeg2d) {
    const footPoint = getPointFromRatio(wallSeg, ratio);
    if (ratio > 0 && ratio < 1 && Point2dUtils.distanceTo(footPoint, point) < globalStores.dynamicAbsorbDistance) {
      return footPoint;
    }

    const startTangentDirection = Point2dUtils.rotateDirection(
      Point2dUtils.getDirection(wallSeg.center, wallSeg.start),
      Math.PI / 2
    );
    const startTangentLine = new Line2d({
      parametric: {
        point: wallSeg.start,
        direction: startTangentDirection
      }
    });
    const startFootPoint = startTangentLine.pointProjectOnLine(point);
    const endTangentDirection = Point2dUtils.rotateDirection(
      Point2dUtils.getDirection(wallSeg.center, wallSeg.end),
      -Math.PI / 2
    );
    const endTangentLine = new Line2d({
      parametric: {
        point: wallSeg.end,
        direction: endTangentDirection
      }
    });
    const endFootPoint = endTangentLine.pointProjectOnLine(point);
    return ratio < 0.5 ? startFootPoint : endFootPoint;
  }

  if (ratio > 0 && ratio < 1) return getPointFromRatio(wallSeg, ratio);
  return getPointFromRatio(wallSeg, ratio);
}

/** 该辅助线是否来自 DXF 底图。底图线只能用于对齐，吸附到它不会与既有墙体形成连接 */
export function isFloorPlanSubline(sublineInfo: ISublineInfo) {
  return sublineInfo.source === EAbsorbSource.FloorPlan;
}

export function getPreferredDisplayAbsorbPointType(type1: EAbsorbPointType, type2: EAbsorbPointType) {
  return getDisplayAbsorbPointTypePriority(type2) > getDisplayAbsorbPointTypePriority(type1) ? type2 : type1;
}

export function getDisplayAbsorbPointTypePriority(type: EAbsorbPointType) {
  switch (type) {
    case EAbsorbPointType.Center:
      return 4;
    case EAbsorbPointType.Start:
    case EAbsorbPointType.End:
      return 3;
    case EAbsorbPointType.Cross:
      return 2;
    case EAbsorbPointType.Foot:
    default:
      return 1;
  }
}
